import type { WorkspaceId } from '../config/workspaces';
import type { IUser } from '../types/auth.types';
import { userService } from '../services/api/user.service';
import { isWorkspaceAccessible } from './resolveDefaultWorkspace';

const STORAGE_PREFIX = 'qumashi-last-workspace:';

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

const VALID_WORKSPACE_IDS = new Set<WorkspaceId>([
  'buyer',
  'seller',
  'creator',
  'owner',
  'staff',
]);

/** Normalize persisted workspace ids. */
export function normalizeWorkspaceId(raw: string | null | undefined): WorkspaceId | null {
  if (!raw) return null;
  return VALID_WORKSPACE_IDS.has(raw as WorkspaceId) ? (raw as WorkspaceId) : null;
}

function parseStoredWorkspace(raw: string | null): WorkspaceId | null {
  return normalizeWorkspaceId(raw);
}

export function readLocalLastWorkspace(userId: string): WorkspaceId | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return parseStoredWorkspace(localStorage.getItem(storageKey(userId)));
  } catch {
    return null;
  }
}

export function writeLocalLastWorkspace(userId: string, workspaceId: WorkspaceId): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(storageKey(userId), workspaceId);
  } catch {
    // Non-blocking
  }
}

export function resolveLastSelectedWorkspace(user: IUser): WorkspaceId | null {
  const fromProfile = normalizeWorkspaceId(user.uiPreferences?.lastSelectedWorkspace ?? null);
  if (fromProfile) return fromProfile;
  return readLocalLastWorkspace(user.id);
}

export async function persistLastSelectedWorkspace(
  user: IUser,
  workspaceId: WorkspaceId,
  options?: { creatorOnboardingComplete?: boolean | null },
): Promise<IUser | null> {
  if (!isWorkspaceAccessible(user, workspaceId, options)) return null;

  writeLocalLastWorkspace(user.id, workspaceId);

  try {
    return await userService.updateUserProfile({
      uiPreferences: { lastSelectedWorkspace: workspaceId },
    });
  } catch {
    return null;
  }
}
